import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../api";

import {
  FiTrash2,
  FiEdit,
  FiEye,
  FiPlus,
  FiSave,
  FiPackage,
  FiClipboard,
  FiSearch,
} from "react-icons/fi";

import { toasts } from "../components/AppToast";
import Pagination from "../components/Pagination";


const EMPTY_FORM = {
  insitem_id: "",
  insdtl_chargeperday: "",
};

export default function InsuranceDetail() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [insuranceItems, setInsuranceItems] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  const [form, setForm] = useState({
    ...EMPTY_FORM,
  });

  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formModal, setFormModal] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [delModal, setDelModal] = useState(null);

  const [delSaving, setDelSaving] = useState(false);

  // Fetch table data
  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const response = await API.get("/insurance", {
        params: {
          page,
          limit,
          search,
        },
      });

      const responseData = response.data;

      if (Array.isArray(responseData)) {
        setData(responseData);
        setTotal(responseData.length);
        setTotalPages(Math.ceil(responseData.length / limit));
      } else {
        setData(responseData.data || []);

        setTotal(Number(responseData.total || 0));

        setTotalPages(
          Number(
            responseData.totalPages ||
              Math.ceil(Number(responseData.total || 0) / limit)
          )
        );
      }
    } catch (error) {
      console.error("fetchData error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to load paid insurance details"
      );

      setData([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, search, limit]);

  // Fetch table whenever page or search changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch paid insurance items
  useEffect(() => {
    const fetchInsuranceItems = async () => {
      setDropdownLoading(true);

      try {
        const response = await API.get("/manage-insurance");

        const responseData = response.data;

        let items = [];

        if (Array.isArray(responseData)) {
          items = responseData;
        } else {
          items = responseData.data || [];
        }

        const paidInsurance = items.filter(
          (item) => Number(item.insitem_type) === 2
        );

        setInsuranceItems(paidInsurance);
      } catch (error) {
        console.error("Insurance dropdown error:", error);

        toasts.error(
          error.response?.data?.error ||
            error.response?.data?.message ||
            "Failed to load paid insurance"
        );

        setInsuranceItems([]);
      } finally {
        setDropdownLoading(false);
      }
    };

    fetchInsuranceItems();
  }, []);

  // Search
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  // Reset form
  const resetForm = () => {
    setForm({
      ...EMPTY_FORM,
    });

    setEditId(null);
  };

  // Open add modal
  const openAddModal = () => {
    resetForm();
    setFormModal(true);
  };

  // Close form modal
  const closeFormModal = () => {
    if (saving) {
      return;
    }

    setFormModal(false);
    resetForm();
  };

  // Edit
  const startEdit = (row) => {
    setForm({
      insitem_id: row.insitem_id || "",
      insdtl_chargeperday: row.insdtl_chargeperday ?? "",
    });

    setEditId(row.insdtl_id);
    setFormModal(true);
  };

  // View
  const handleView = (row) => {
    setViewModal(row);
  };

  // Form change
  const handleChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  // Submit
  const handleSubmit = async () => {
    if (!form.insitem_id) {
      toasts.error("Insurance name is required");
      return;
    }

    if (
      form.insdtl_chargeperday === "" ||
      form.insdtl_chargeperday === null
    ) {
      toasts.error("Price is required");
      return;
    }

    if (Number(form.insdtl_chargeperday) < 0) {
      toasts.error("Price cannot be negative");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        insitem_id: Number(form.insitem_id),
        insdtl_type: 2,
        insdtl_chargeperday: Number(form.insdtl_chargeperday),
      };

      if (editId) {
        await API.put(`/insurance/${editId}`, payload);

        toasts.success("Paid insurance details updated successfully");
      } else {
        await API.post("/insurance", payload);

        toasts.success("Paid insurance details added successfully");
      }

      setFormModal(false);
      resetForm();

      await fetchData();
    } catch (error) {
      console.error("Submit error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Operation failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!delModal) {
      return;
    }

    setDelSaving(true);

    try {
      await API.delete(`/insurance/${delModal.insdtl_id}`);

      toasts.success("Paid insurance details deleted successfully");

      setDelModal(null);

      if (data.length === 1 && page > 1) {
        setPage((previous) => previous - 1);
      } else {
        await fetchData();
      }
    } catch (error) {
      console.error("Delete error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Delete failed"
      );
    } finally {
      setDelSaving(false);
    }
  };


 // =========================================================
  // PAGINATION - PREVIOUS
  // =========================================================

  const handlePrevious = () => {
    if (
      loading ||
      page <= 1
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.max(
          1,
          prev - 1
        )
    );
  };

  // =========================================================
  // PAGINATION - NEXT
  // =========================================================

  const handleNext = () => {
    if (
      loading ||
      totalPages <= 0 ||
      page >= totalPages
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.min(
          totalPages,
          prev + 1
        )
    );
  };





  return (
    <Layout activePage="Insurance">
      <main className="dashboard-main">
        <div className="table-card">
          <div className="table-card-head">
            <h3 className="table-card-title d-flex align-items-center">
              <FiClipboard size={26} className="me-2" />

              <span>Manage Paid Insurance Details</span>

              <span className="rec-count ms-1">{total} items</span>
            </h3>

            <div className="search-wrapper">
              <span className="search-icon">
                <FiSearch />
              </span>

              <input
                type="text"
                placeholder="Search ..."
                value={search}
                onChange={handleSearch}
                className="search-input"
              />

              {search && (
                <button
                  type="button"
                  className="season-clear-btn"
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            <button
              type="button"
              className="um-btn-primary"
              onClick={openAddModal}
            >
              <span className="um-add-icon">＋</span>
              Add Paid Insurance
            </button>
          </div>

          <div className="table-responsive">
            <table className="um-table">
              <thead>
                <tr className="um-table-head">
                  <th className="um-th text-center">#</th>
                  <th className="um-th text-center">Insurance Name</th>
                  <th className="um-th text-center">Price</th>
                  <th className="um-th text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({ length: 4 }).map(
                        (_, cellIndex) => (
                          <td
                            key={cellIndex}
                            className="um-td text-center"
                          >
                            <div className="skeleton" />
                          </td>
                        )
                      )}
                    </tr>
                  ))
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="empty-table-cell text-center py-5"
                    >
                      <div className="empty-icon">
                        <FiPackage />
                      </div>

                      <div className="empty-title">
                        No paid insurance details found
                      </div>

                      <div className="empty-sub">
                        Use the Add Paid Insurance button to add one
                      </div>
                    </td>
                  </tr>
                ) : (
                  data.map((row, index) => (
                    <tr
                      key={row.insdtl_id}
                      className={`um-row ${
                        index % 2 === 0 ? "even-row" : "odd-row"
                      }`}
                    >
                      <td className="um-td text-center">
                        {(page - 1) * limit + index + 1}
                      </td>

                      <td className="um-td text-center">
                        <strong>{row.insitem_name || "—"}</strong>
                      </td>

                      <td className="um-td text-center">
                        <strong>
                          ₹ {row.insdtl_chargeperday ?? "0"}
                        </strong>
                      </td>

                      <td className="um-td text-center">
                        <div
                          className="action-buttons"
                          style={{
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <button
                            type="button"
                            className="action-btn action-view"
                            title="View"
                            onClick={() => handleView(row)}
                          >
                            <FiEye size={15} />
                          </button>

                          <button
                            type="button"
                            className="action-btn action-edit"
                            title="Edit"
                            onClick={() => startEdit(row)}
                          >
                            <FiEdit size={15} />
                          </button>

                          <button
                            type="button"
                            className="action-btn action-delete delete-enabled"
                            title="Delete"
                            onClick={() => setDelModal(row)}
                          >
                            <FiTrash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <Pagination
  page={page}
  totalPages={totalPages}
  total={total}
  limit={10}
  loading={loading}
  onPrevious={handlePrevious}
  onNext={handleNext}
/>
          </div>
        </div>

        {formModal && (
          <div className="um-overlay" onClick={closeFormModal}>
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="um-modal-head">
                <div className="d-flex align-items-center gap-2">
                  <div className="form-card-icon">
                    {editId ? (
                      <FiEdit size={24} />
                    ) : (
                      <FiPlus size={24} />
                    )}
                  </div>

                  <div>
                    <h2 className="um-modal-title mb-0">
                      {editId
                        ? "Edit Paid Insurance"
                        : "Add Paid Insurance"}
                    </h2>

                    <p className="form-card-sub mb-0">
                      Manage paid insurance details
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  ✕
                </button>
              </div>

              <div className="um-modal-scroll">
                <div className="row g-3">
                  <div className="col-md-8">
                    <label className="field-label">
                      Insurance Name *
                    </label>

                    <select
                      className="rd-input"
                      value={form.insitem_id}
                      onChange={(e) =>
                        handleChange(
                          "insitem_id",
                          e.target.value
                        )
                      }
                      disabled={dropdownLoading}
                    >
                      <option value="">
                        Select Insurance
                      </option>

                      {insuranceItems
                        .filter(
                          (item) =>
                            Number(item.insitem_type) === 2
                        )
                        .map((item) => (
                          <option
                            key={item.insitem_id}
                            value={item.insitem_id}
                          >
                            {item.insitem_name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="field-label">
                      Rental Price Per Day *
                    </label>

                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="rd-input"
                      placeholder="Enter price"
                      value={form.insdtl_chargeperday}
                      onChange={(e) =>
                        handleChange(
                          "insdtl_chargeperday",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? (
                    <span className="spinner" />
                  ) : editId ? (
                    <>
                      <FiSave />
                      Update Insurance
                    </>
                  ) : (
                    <>
                      <FiPlus />
                      Add Insurance
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {viewModal && (
          <div
            className="um-overlay"
            onClick={() => setViewModal(null)}
          >
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="um-modal-head">
                <div>
                  <h2 className="um-modal-title mb-0">
                    <FiEye /> Paid Insurance Details
                  </h2>

                  <p className="form-card-sub mb-0">
                    View insurance information
                  </p>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={() => setViewModal(null)}
                >
                  ✕
                </button>
              </div>

              <div className="um-modal-scroll">
                <div className="row g-3">
                  <div className="col-md-12">
                    <div className="small text-muted text-uppercase fw-semibold mb-1">
                      Insurance Name
                    </div>

                    <div className="bg-light rounded-3 border p-3">
                      <div className="fw-bold">
                        {viewModal.insitem_name || "—"}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-12">
                    <div className="small text-muted text-uppercase fw-semibold mb-1">
                      Rental Price Per Day
                    </div>

                    <div className="bg-light rounded-3 border p-3">
                      <div className="fw-bold">
                        ₹ {viewModal.insdtl_chargeperday ?? "0"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={() => setViewModal(null)}
                >
                  Close
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={() => {
                    const selected = viewModal;

                    setViewModal(null);

                    setTimeout(() => {
                      startEdit(selected);
                    }, 100);
                  }}
                >
                  <FiEdit />
                  Edit Insurance
                </button>
              </div>
            </div>
          </div>
        )}

        {delModal && (
          <div
            className="um-overlay"
            onClick={() => setDelModal(null)}
          >
            <div
              className="um-modal um-delete-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-content">
                <div className="delete-icon">
                  <FiTrash2 />
                </div>

                <h2 className="delete-title">
                  Delete Paid Insurance?
                </h2>

                <div className="delete-badges">
                  <span className="badge badge-success">
                    {delModal.insitem_name || "Insurance"}
                  </span>
                </div>

                <div className="mt-2">
                  <strong>
                    ₹ {delModal.insdtl_chargeperday ?? "0"}
                  </strong>
                </div>

                <p className="delete-description">
                  This paid insurance detail will be permanently
                  deleted.
                  <br />
                  <strong>
                    This action cannot be undone.
                  </strong>
                </p>

                <div className="delete-actions">
                  <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={() => setDelModal(null)}
                    disabled={delSaving}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    disabled={delSaving}
                    className="delete-confirm-btn"
                    onClick={handleDelete}
                  >
                    {delSaving ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <FiTrash2 size={16} />
                        Delete Permanently
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </main>
    </Layout>
  );
}
